import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Search } from "@mui/icons-material";
import { UserCheck, Users, Building2, Settings, X, Activity, UserX, Copy } from "lucide-react";
import Loader from "../Loader.jsx";
import { adminDashboardService } from "./services/adminDashboardService";
import { useImpersonation } from "./hooks/useImpersonation";

const ITEMS_PER_PAGE = 15;

const StatCard = ({ active, onClick, icon: Icon, iconBg, label, count }) => (
  <div
    className={`px-3 py-2.5 rounded-xl cursor-pointer border transition-colors duration-150 bg-gradient-to-br from-[rgba(34,52,97,0.25)] to-[rgba(11,10,31,0.05)] ${
      active ? "border-purple-500/50 ring-1 ring-purple-500/30" : "border-white/10"
    }`}
    onClick={onClick}
  >
    <div className="flex items-center gap-2">
      <div className={`p-1.5 flex-shrink-0 ${iconBg} rounded-lg`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <p className="text-gray-400 text-xs leading-tight truncate">{label}</p>
        <p className="text-white text-base font-semibold leading-tight">{count}</p>
      </div>
    </div>
  </div>
);

const Toggle = ({ value, onChange }) => (
  <button
    onClick={onChange}
    className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${value ? "bg-purple-500" : "bg-gray-600"}`}
  >
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${value ? "translate-x-5" : "translate-x-0"}`} />
  </button>
);

const ImpersonationContent = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [impersonatingUserId, setImpersonatingUserId] = useState(null);
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [activePeriod, setActivePeriod] = useState("1d"); // "1d" | "7d" | "1m"
  const [showNeverLoggedIn, setShowNeverLoggedIn] = useState(false);
  const [configModal, setConfigModal] = useState(null); // { user, liveAvatarEnabled, interactivityEnabled }
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);

  const { startImpersonation, isLoading: impersonationLoading } = useImpersonation();

  const isActiveWithin = (lastLogin, period = "1d") => {
    if (!lastLogin) return false;
    const login = new Date(lastLogin);
    const now = new Date();
    if (period === "1d") {
      return (
        login.getFullYear() === now.getFullYear() &&
        login.getMonth() === now.getMonth() &&
        login.getDate() === now.getDate()
      );
    }
    const diffMs = now - login;
    if (period === "7d") return diffMs <= 7 * 24 * 60 * 60 * 1000;
    if (period === "1m") return diffMs <= 30 * 24 * 60 * 60 * 1000;
    return false;
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await adminDashboardService.getImpersonatableUsers();
      setUsers(response.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const defaultTestPerms = { mcq_enabled: false, coding_enabled: false, behavioral_enabled: false, typing_enabled: true };

  const handleOpenConfig = async (user) => {
    setConfigLoading(true);
    setConfigModal({ user, liveAvatarEnabled: false, interactivityEnabled: false, interviewV1Animation: true, testPermissions: { ...defaultTestPerms } });
    try {
      const data = await adminDashboardService.getOrgConfig(user.user_id);
      setConfigModal({
        user,
        liveAvatarEnabled: data.live_avatar_enabled,
        interactivityEnabled: data.interactivity_enabled,
        interviewV1Animation: data.interview_v1_animation ?? true,
        testPermissions: { ...defaultTestPerms, ...(data.test_permissions || {}) },
      });
    } catch (error) {
      toast.error("Failed to load configuration");
      setConfigModal(null);
    } finally {
      setConfigLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!configModal) return;
    setConfigSaving(true);
    try {
      await adminDashboardService.updateOrgConfig(configModal.user.user_id, {
        liveAvatarEnabled: configModal.liveAvatarEnabled,
        interactivityEnabled: configModal.interactivityEnabled,
        interviewV1Animation: configModal.interviewV1Animation,
        testPermissions: configModal.testPermissions,
      });
      toast.success("Configuration saved successfully");
      setConfigModal(null);
    } catch (error) {
      toast.error("Failed to save configuration");
    } finally {
      setConfigSaving(false);
    }
  };

  const toggleTestPerm = (key) =>
    setConfigModal(prev => ({ ...prev, testPermissions: { ...prev.testPermissions, [key]: !prev.testPermissions[key] } }));

  const handleImpersonate = async (user) => {
    if (user.is_suspended) {
      toast.error("Cannot impersonate a suspended user");
      return;
    }

    setImpersonatingUserId(user.user_id);
    const result = await startImpersonation(user.user_id, user.user_type);

    if (!result.success) {
      toast.error(result.error || "Failed to start impersonation");
      setImpersonatingUserId(null);
    }
  };

  // Filter users
  const filteredUsers = users.filter((user) => {
    if (user.is_suspended) return false;
    const search = searchTerm.toLowerCase();
    const matchesSearch = !search ||
      user.name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.organization_name?.toLowerCase().includes(search);
    const matchesType = filterType === "all" || user.user_type === filterType;
    if (showActiveOnly && !isActiveWithin(user.last_login, activePeriod)) return false;
    if (showNeverLoggedIn && user.last_login) return false;
    return matchesSearch && matchesType;
  });

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Stats — single pass
  const stats = users.reduce(
    (acc, u) => {
      if (u.user_type === "Company_Admin") acc.admins++;
      else if (u.user_type === "HR") acc.hrs++;
      if (isActiveWithin(u.last_login, activePeriod)) acc.activeToday++;
      if (!u.last_login) acc.neverLoggedIn++;
      return acc;
    },
    { admins: 0, hrs: 0, activeToday: 0, neverLoggedIn: 0 }
  );

  if (loading) return <Loader />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-white">Impersonate Users</h1>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <StatCard
          active={filterType === "all" && !showActiveOnly && !showNeverLoggedIn}
          onClick={() => { setFilterType("all"); setShowActiveOnly(false); setShowNeverLoggedIn(false); setCurrentPage(1); }}
          icon={Users}
          iconBg="bg-purple-500/20 text-purple-400"
          label="Total Users"
          count={users.length}
        />
        <StatCard
          active={filterType === "Company_Admin" && !showActiveOnly && !showNeverLoggedIn}
          onClick={() => { setFilterType("Company_Admin"); setShowActiveOnly(false); setShowNeverLoggedIn(false); setCurrentPage(1); }}
          icon={Building2}
          iconBg="bg-blue-500/20 text-blue-400"
          label="Organization Admins"
          count={stats.admins}
        />
<StatCard
          active={showActiveOnly}
          onClick={() => { setShowActiveOnly(v => !v); setShowNeverLoggedIn(false); setFilterType("all"); setCurrentPage(1); }}
          icon={Activity}
          iconBg="bg-green-500/20 text-green-400"
          label={showActiveOnly
            ? activePeriod === "7d" ? "Active (7 Days)"
            : activePeriod === "1m" ? "Active (30 Days)"
            : "Active Today"
            : "Active Today"}
          count={stats.activeToday}
        />
        <StatCard
          active={showNeverLoggedIn}
          onClick={() => { setShowNeverLoggedIn(v => !v); setShowActiveOnly(false); setFilterType("all"); setCurrentPage(1); }}
          icon={UserX}
          iconBg="bg-orange-500/20 text-orange-400"
          label="Never Logged In"
          count={stats.neverLoggedIn}
        />
      </div>

      {/* Active Period Pills */}
      {showActiveOnly && (
        <div className="flex items-center gap-2 -mt-2">
          <span className="text-gray-400 text-xs">Active in:</span>
          {[
            { value: "1d", label: "Today" },
            { value: "7d", label: "7 Days" },
            { value: "1m", label: "30 Days" },
          ].map(({ value, label }) => (
            <button
              key={value}
              onClick={() => { setActivePeriod(value); setCurrentPage(1); }}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                activePeriod === value
                  ? "bg-green-500/30 text-green-300 border border-green-500/40"
                  : "bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Search Bar */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by name, email, or organization..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className="w-full pl-10 pr-4 py-2.5 bg-transparent border border-gray-700/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500/50"
          />
        </div>
        <button
          onClick={fetchUsers}
          className="px-4 py-2.5 bg-gray-700/50 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="rounded-xl overflow-x-auto bg-gradient-to-br from-[rgba(34,52,97,0.25)] to-[rgba(11,10,31,0.05)] border border-white/10">
        <table className="w-full min-w-[780px] text-sm">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left px-3 py-2.5 text-gray-400 font-medium">Name</th>
              <th className="text-left px-3 py-2.5 text-gray-400 font-medium">Email</th>
              <th className="text-left px-3 py-2.5 text-gray-400 font-medium">Role</th>
              <th className="text-left px-3 py-2.5 text-gray-400 font-medium">Organization</th>
              <th className="text-left px-3 py-2.5 text-gray-400 font-medium">Last Login</th>
              <th className="text-left px-3 py-2.5 text-gray-400 font-medium">Action</th>
              <th className="text-left px-3 py-2.5 text-gray-400 font-medium">Settings</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-8 text-center text-gray-400">
                  {searchTerm || filterType !== "all" || showActiveOnly || showNeverLoggedIn
                    ? "No users match your search criteria"
                    : "No users available"}
                </td>
              </tr>
            ) : (
              paginatedUsers.map((user) => (
                <tr key={user.user_id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 flex-shrink-0 rounded-full bg-gradient-to-r from-pink-500 to-blue-500 flex items-center justify-center text-white text-xs font-medium">
                        {user.name?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-white text-sm">{user.name || "N/A"}</span>
                          {isActiveWithin(user.last_login, activePeriod) && (
                            <span className="px-1.5 py-0.5 text-xs bg-green-500/20 text-green-400 rounded-full whitespace-nowrap">● Active</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 max-w-[160px]">
                    <div className="flex items-center gap-1 group">
                      <span className="text-gray-300 truncate text-sm">{user.email || "N/A"}</span>
                      {user.email && (
                        <button
                          onClick={() => { navigator.clipboard.writeText(user.email); toast.success("Email copied"); }}
                          className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded text-gray-500 hover:text-gray-300"
                          title="Copy email"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${user.user_type === "Company_Admin"
                        ? "bg-purple-500/20 text-purple-300"
                        : "bg-blue-500/20 text-blue-300"
                      }`}>
                      {user.user_type === "Company_Admin" ? "Org Admin" : "HR"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-300 max-w-[140px] truncate">{user.organization_name || "N/A"}</td>
                  <td className="px-3 py-2 text-gray-400 text-xs whitespace-nowrap">
                    {user.last_login
                      ? new Date(user.last_login).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })
                      : <span className="text-gray-600">Never</span>}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => handleImpersonate(user)}
                      disabled={user.is_suspended || impersonatingUserId === user.user_id || impersonationLoading}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 whitespace-nowrap ${user.is_suspended
                          ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                          : impersonatingUserId === user.user_id
                            ? "bg-yellow-500/20 text-yellow-300"
                            : "bg-gradient-to-r from-pink-500 to-blue-500 text-white hover:opacity-90"
                        }`}
                    >
                      <UserCheck className="w-3.5 h-3.5" />
                      {impersonatingUserId === user.user_id ? "Switching..." : "Login As"}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    {user.user_type === "Company_Admin" && (
                      <button
                        onClick={() => handleOpenConfig(user)}
                        className="p-1.5 rounded-lg bg-gray-700/50 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                        title="Configure settings"
                      >
                        <Settings className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <span className="text-gray-400 text-xs">
          {filteredUsers.length === 0
            ? "0 users"
            : `${(currentPage - 1) * ITEMS_PER_PAGE + 1}–${Math.min(currentPage * ITEMS_PER_PAGE, filteredUsers.length)} of ${filteredUsers.length} users`}
        </span>
        {totalPages > 1 && (
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded bg-gray-700/50 text-gray-300 disabled:opacity-50"
            >
              Prev
            </button>
            <span className="px-3 py-1 text-gray-400">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded bg-gray-700/50 text-gray-300 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Info Note */}
      <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20">
        <p className="text-gray-300 text-sm">
          <strong className="text-blue-400">Note:</strong> When you impersonate a user, you will be
          redirected to their dashboard. Click "Exit Impersonation" in the header to return.
        </p>
      </div>

      {/* Config Modal */}
      {configModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm mx-4 rounded-2xl bg-gradient-to-br from-[rgba(34,52,97,0.95)] to-[rgba(11,10,31,0.95)] border border-white/10 p-6 shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-white font-semibold text-lg">Organization Settings</h2>
                <p className="text-gray-400 text-sm mt-0.5">{configModal.user.name}</p>
              </div>
              <button
                onClick={() => setConfigModal(null)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {configLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Live Avatar Toggle */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                  <div>
                    <p className="text-white text-sm font-medium">Live Avatar</p>
                    <p className="text-gray-400 text-xs mt-0.5">Enable AI avatar during interviews</p>
                  </div>
                  <Toggle value={configModal.liveAvatarEnabled} onChange={() => setConfigModal(prev => ({ ...prev, liveAvatarEnabled: !prev.liveAvatarEnabled }))} />
                </div>

                {/* Interactivity Toggle */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                  <div>
                    <p className="text-white text-sm font-medium">Interactivity</p>
                    <p className="text-gray-400 text-xs mt-0.5">Enable interactive follow-up questions</p>
                  </div>
                  <Toggle value={configModal.interactivityEnabled} onChange={() => setConfigModal(prev => ({ ...prev, interactivityEnabled: !prev.interactivityEnabled }))} />
                </div>

                {/* Interview V1 Animation */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                  <div>
                    <p className="text-white text-sm font-medium">Interview Animation</p>
                    <p className="text-gray-400 text-xs mt-0.5">Enable v1 animation during interviews</p>
                  </div>
                  <Toggle value={configModal.interviewV1Animation} onChange={() => setConfigModal(prev => ({ ...prev, interviewV1Animation: !prev.interviewV1Animation }))} />
                </div>

                {/* Test Permissions */}
                <div className="rounded-xl bg-white/5 border border-white/10 overflow-hidden">
                  <p className="text-gray-400 text-xs font-medium px-4 pt-3 pb-2 border-b border-white/10 uppercase tracking-wide">Test Permissions</p>
                  {[
                    { key: "mcq_enabled", label: "MCQ" },
                    { key: "coding_enabled", label: "Coding" },
                    { key: "behavioral_enabled", label: "Behavioral" },
                    { key: "typing_enabled", label: "Typing" },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center justify-between px-4 py-3 border-b border-white/5 last:border-0">
                      <p className="text-white text-sm">{label}</p>
                      <Toggle value={configModal.testPermissions[key]} onChange={() => toggleTestPerm(key)} />
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setConfigModal(null)}
                    className="flex-1 px-4 py-2.5 rounded-lg bg-gray-700/50 text-gray-300 hover:bg-gray-700 transition-colors text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveConfig}
                    disabled={configSaving}
                    className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-pink-500 to-blue-500 text-white hover:opacity-90 transition-opacity text-sm font-medium disabled:opacity-50"
                  >
                    {configSaving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImpersonationContent;
